/**
 * @file BillingTypeDropdown.js
 * @description A dropdown component for selecting a billing type.
 */

import React, { useState, useEffect } from 'react';
import { fetchBillingTypes } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';

/**
 * A dropdown component for selecting a billing type.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {number} props.selectedBillingType - The ID of the selected billing type.
 * @param {Function} props.onBillingTypeChange - The callback function to be called when the selection changes.
 * @param {string} [props.label="Billing Type"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @returns {JSX.Element} The rendered BillingTypeDropdown component.
 */
const BillingTypeDropdown = ({
    className,
    inlineLabel = true,
    selectedBillingType,
    onBillingTypeChange,
    label = 'Billing Type',
    isRequired = false,
}) => {
    const [billingTypes, setBillingTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getBillingTypes = async () => {
            setIsLoading(true);
            try {
                const data = await fetchBillingTypes();
                setBillingTypes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Could not fetch billing types:', error);
                Toast.error('Unable to load billing types.');
            } finally {
                setIsLoading(false);
            }
        };

        getBillingTypes();
    }, []);

    const getDefaultLabel = () => {
        const matchingType = billingTypes.find(
            type => type.id === selectedBillingType,
        );
        return matchingType ? matchingType.name : 'Select Billing Type';
    };

    const billingTypeOptions = billingTypes.map(type => ({
        value: type.id,
        label: type.name,
    }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="billing-type-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="billing-type-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={billingTypeOptions}
                    value={selectedBillingType}
                    onChange={onBillingTypeChange}
                    disabled={billingTypes.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default BillingTypeDropdown;
